import React from 'react';

/**
 * Logo Kanea en SVG inline.
 *
 * Pourquoi inline plutôt qu'un `<img src="…svg">` : l'app a un thème sombre
 * ([data-theme='dark']). L'ancien `images/logo.png` était un PNG à fond blanc
 * OPAQUE, qui s'affichait en rectangle blanc dans les surfaces sombres ; un
 * `<img>` pointant vers le SVG noir serait pire encore (invisible sur fond
 * sombre). En inline, le tracé hérite de `currentColor`, donc de la couleur de
 * texte du conteneur, et suit le thème sans effort.
 *
 * Deux variantes, selon la place disponible :
 *  - `mark` : le « K » fléché seul. À utiliser dès que le logo est petit
 *    (< ~50px) ou déjà accompagné du texte « KaneaBourse », sinon le lettrage
 *    devient illisible ou redondant.
 *  - `full` : le « K » + le lettrage « KANEA ». Réservé aux affichages larges.
 *
 * Les tracés proviennent de `ui/public/images/kanea-{mark,logo}.svg`, générés
 * par `scripts/pwa-icons/generate_icons.py` à partir de `logo-source.png`.
 * NE PAS les modifier à la main : `tests/test_pwa_icons.py` vérifie qu'ils
 * restent identiques aux SVG générés.
 */

export type KaneaLogoVariant = 'horizontal' | 'mark' | 'full' | 'badge';

export interface KaneaLogoProps {
  variant?: KaneaLogoVariant;
  className?: string;
  style?: React.CSSProperties;
  /** Nom accessible. `null` marque le logo comme décoratif (texte adjacent). */
  title?: string | null;
  /** Couleur du mark « K » (défaut : #6C3FC4 ou inherit) */
  markColor?: string;
  /** Couleur du texte KaneaBourse (en mode horizontal) */
  textColor?: string;
  /** Hauteur / taille du logo en pixels ou unité CSS */
  height?: number | string;
  /** Encapsuler le mark dans une pastille/badge blanche (comme dans l'auth panel) */
  badge?: boolean;
}

const MARK_VIEWBOX = '0 0 96.155 100.570';
const MARK_PATH =
  'M52.502,2.053L86.502,2.000L88.176,2.699L87.982,4.699L86.927,6.699L71.502,32.588L70.502,33.168' +
  'L69.502,32.916L65.196,26.699L64.502,26.157L62.502,26.419L6.502,62.841L4.502,63.965L2.502,63.990' +
  'L2.000,62.699L2.000,4.699L2.494,3.699L19.502,3.692L20.344,4.699L20.391,33.699L21.502,34.272' +
  'L54.702,12.699L55.665,10.699L52.034,4.699L51.731,2.699Z ' +
  'M48.502,41.514L50.502,41.182L52.808,43.699L92.759,95.699L94.155,97.699L93.502,98.570L72.502,98.395' +
  'L46.726,64.699L45.502,63.485L44.502,63.244L40.502,65.339L16.502,81.092L2.502,89.595L2.000,88.699' +
  'L2.137,71.699Z';

const FULL_VIEWBOX = '0 0 126.793 147.145';
const FULL_PATH =
  'M66.441,2.053L100.441,2.000L102.114,2.699L101.921,4.699L100.866,6.699L85.441,32.588L84.441,33.168' +
  'L83.441,32.916L79.135,26.699L78.441,26.157L76.441,26.419L20.441,62.841L18.441,63.965L16.441,63.990' +
  'L15.939,62.699L15.939,4.699L16.433,3.699L33.441,3.692L34.283,4.699L34.329,33.699L35.441,34.272' +
  'L68.641,12.699L69.604,10.699L65.972,4.699L65.669,2.699Z ' +
  'M62.441,41.514L64.441,41.182L66.747,43.699L106.698,95.699L108.094,97.699L107.441,98.570L86.441,98.395' +
  'L60.665,64.699L59.441,63.485L58.441,63.244L54.441,65.339L30.441,81.092L16.441,89.595L15.939,88.699' +
  'L16.076,71.699Z ' +
  'M2.441,125.799L3.441,125.772L4.514,126.699L4.525,133.699L5.441,134.368L12.441,126.553L14.441,125.772' +
  'L15.284,126.699L9.028,133.699L8.874,134.699L16.441,144.759L14.441,145.145L13.554,144.699L7.441,136.769' +
  'L6.441,136.466L4.621,138.699L4.514,144.699L3.441,145.145L2.000,144.699L2.000,126.699Z ' +
  'M34.441,125.797L35.441,125.772L36.671,126.699L43.441,144.792L41.441,145.127L40.984,144.699' +
  'L39.561,140.699L38.441,140.092L31.441,140.101L30.822,140.699L29.270,144.699L28.441,145.145' +
  'L26.892,144.699L33.239,127.699Z ' +
  'M56.441,125.772L58.441,126.130L58.888,126.699L67.441,139.826L68.122,138.699L68.131,126.699' +
  'L68.441,126.059L69.441,125.772L70.441,126.123L70.441,144.978L68.441,145.135L67.908,144.699' +
  'L60.774,133.699L58.441,131.033L58.139,144.699L57.441,145.145L55.858,144.699L55.858,126.699Z ' +
  'M85.441,125.772L96.441,125.772L96.902,126.699L96.441,127.772L88.441,127.772L87.121,128.699' +
  'L87.441,133.921L94.441,134.189L95.750,134.699L95.441,135.994L87.441,136.377L87.121,136.699' +
  'L87.207,142.699L88.441,143.197L96.441,143.197L96.980,143.699L96.980,144.699L96.441,145.145' +
  'L85.441,145.145L84.858,144.699L84.858,126.699Z ' +
  'M115.441,125.968L117.441,126.050L117.863,126.699L124.793,144.699L124.441,145.051L122.441,144.990' +
  'L120.854,140.699L119.441,140.092L112.441,140.188L110.661,144.699L109.441,145.145L108.033,144.699Z ' +
  'M35.441,129.578L34.403,130.699L32.130,136.699L32.037,137.699L32.441,138.096L37.441,138.126' +
  'L38.138,137.699L37.799,135.699Z ' +
  'M116.441,129.412L114.107,134.699L113.441,137.903L118.441,138.126L119.546,137.699Z';

export const KaneaLogo: React.FC<KaneaLogoProps> = ({
  variant = 'horizontal',
  className = '',
  style,
  title = 'KaneaBourse',
  markColor = '#6C3FC4',
  textColor,
  height,
  badge = false,
}) => {
  const markStyle: React.CSSProperties = {
    color: markColor,
    height: typeof height === 'number' ? `${height}px` : height || undefined,
    width: 'auto',
    display: 'block',
    flexShrink: 0,
  };

  const renderMarkSvg = (overrideStyle?: React.CSSProperties) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox={MARK_VIEWBOX}
      className={`kanea-logo kanea-logo-mark ${className}`}
      style={{ ...markStyle, ...overrideStyle }}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      focusable='false'
    >
      {title ? <title>{title}</title> : null}
      <path
        fill='currentColor'
        fillRule='evenodd'
        d={MARK_PATH}
      />
    </svg>
  );

  // Variante badge : mark dans une pastille arrondie blanche (ex. auth desktop panel)
  if (variant === 'badge' || (variant === 'mark' && badge)) {
    return (
      <div
        className={`kanea-logo-badge ${className}`}
        style={{
          background: '#ffffff',
          borderRadius: '10px',
          padding: '6px 10px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
          ...style,
        }}
      >
        {renderMarkSvg({ color: markColor || '#1E1B2E', height: height || '28px' })}
      </div>
    );
  }

  // Variante Mark seul
  if (variant === 'mark') {
    return renderMarkSvg(style);
  }

  // Variante Full vertical historique (rétrocompatibilité)
  if (variant === 'full') {
    return (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox={FULL_VIEWBOX}
        className={`kanea-logo kanea-logo-full ${className}`}
        style={{ color: markColor, ...style }}
        role={title ? 'img' : undefined}
        aria-hidden={title ? undefined : true}
        focusable='false'
      >
        {title ? <title>{title}</title> : null}
        <path
          fill='currentColor'
          fillRule='evenodd'
          d={FULL_PATH}
        />
      </svg>
    );
  }

  // Variante Horizontal (Mark + KaneaBourse)
  const resolvedTextColor = textColor || markColor || '#6C3FC4';
  const calculatedHeight = typeof height === 'number' ? `${height}px` : height || '34px';

  return (
    <div
      className={`kanea-brand-horizontal ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        textDecoration: 'none',
        lineHeight: 1,
        ...style,
      }}
      role={title ? 'img' : undefined}
      aria-label={title || 'KaneaBourse'}
    >
      {badge ? (
        <div
          style={{
            background: '#ffffff',
            borderRadius: '8px',
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          }}
        >
          {renderMarkSvg({ color: markColor || '#1E1B2E', height: calculatedHeight })}
        </div>
      ) : (
        renderMarkSvg({ color: markColor, height: calculatedHeight })
      )}
      <span
        className='kanea-brand-wordmark'
        style={{
          fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontSize: typeof height === 'number' ? `${Math.max(16, Math.round(height * 0.68))}px` : '1.35rem',
          fontWeight: 800,
          letterSpacing: '-0.025em',
          color: resolvedTextColor,
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        KaneaBourse
      </span>
    </div>
  );
};

export default KaneaLogo;
